import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTimelineContext } from "@/machines/context/timeline-context";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";

/**
 * Consume a `?ts=<epochMs>` query param on the machine-detail page and seek the
 * timeline to that moment exactly once.
 *
 * Used by the ML-export review "Open in machine timeline" link. The target
 * timestamp may fall outside the page's current date window (the default is
 * the last 24h), so we first widen the window around `ts` — which makes the
 * events query refetch — and only then, once the timeline has loaded, set the
 * cursor and centre the view. The param is cleared afterwards so a re-render,
 * refresh, or back-navigation doesn't re-trigger the seek.
 *
 * On top of selecting the time it also requests the existing on-map event
 * overlay for the event at that moment (the timeline section resolves + opens
 * it). The page is intentionally NOT scrolled — it stays where it is on arrival.
 */
const WINDOW_MS = 30 * 60 * 1000; // ±30 min around the target

export const useSeekToTimestampParam = (isTimelineLoading: boolean) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tsParam = searchParams.get("ts");
  const { setSelectedTime, requestCenter, requestEventOverlay } = useTimelineContext();
  const { value, setValue, setSelectedPresetKey } = useDateTimeRangeStore();

  // Tracks how far we've progressed for the current ts value:
  //   `range:<ts>` = we've widened the window, waiting for reload
  //   `seek:<ts>`  = we've seeked, done
  const handledRef = useRef<string | null>(null);

  const clearParam = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("ts");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!tsParam) return;

    const ts = Number(tsParam);
    if (!Number.isFinite(ts)) {
      clearParam();
      return;
    }

    const inRange =
      ts >= value.fromDate.getTime() && ts <= value.toDate.getTime();

    if (!inRange) {
      // Widen the window ONCE for this ts; the events query then refetches.
      if (handledRef.current !== `range:${tsParam}`) {
        handledRef.current = `range:${tsParam}`;
        setValue({
          fromDate: new Date(ts - WINDOW_MS),
          toDate: new Date(ts + WINDOW_MS),
        });
        setSelectedPresetKey(null);
      }
      return;
    }

    // In range — wait for the timeline to finish loading, then seek once.
    if (isTimelineLoading) return;
    if (handledRef.current === `seek:${tsParam}`) return;
    handledRef.current = `seek:${tsParam}`;

    // Select the time on the timeline and ask the section to open the existing
    // on-map event overlay for the event there. No page scroll.
    setSelectedTime(ts, "external");
    requestCenter();
    requestEventOverlay(ts);
    clearParam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tsParam, value, isTimelineLoading]);
};
